import type { SelectAction, ActionResult } from '../types/actions';
import { isElementVisible, isElementEnabled, scrollIntoView } from '../utils/dom-utils';
import { waitForElement } from '../utils/wait-utils';

export async function executeSelectAction(action: SelectAction): Promise<ActionResult> {
  const startTime = Date.now();

  try {
    // Find the select element
    let element = document.querySelector<HTMLSelectElement>(action.selector);

    // Wait for element if specified
    if (!element && action.waitForElement) {
      element = await waitForElement<HTMLSelectElement>(
        action.selector,
        action.waitForElement
      );
    }

    if (!element) {
      return {
        success: false,
        action,
        error: `Select element not found: ${action.selector}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: false
      };
    }

    // Verify it's a select element
    if (!(element instanceof HTMLSelectElement)) {
      return {
        success: false,
        action,
        error: 'Element is not a select element',
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Check if visible and enabled
    if (!isElementVisible(element)) {
      return {
        success: false,
        action,
        error: 'Select is not visible',
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    if (!isElementEnabled(element)) {
      return {
        success: false,
        action,
        error: 'Select is disabled',
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Scroll into view if needed
    if (action.scrollIntoView !== false) {
      scrollIntoView(element);
    }

    // Find the option to select
    const option = Array.from(element.options).find(
      opt => opt.value === action.value || opt.text === action.value
    );

    if (!option) {
      return {
        success: false,
        action,
        error: `Option not found: ${action.value}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Focus the element
    element.focus();

    // Set the value
    element.value = option.value;

    // Trigger change event
    const changeEvent = new Event('change', { bubbles: true, cancelable: true });
    element.dispatchEvent(changeEvent);

    // Trigger input event
    const inputEvent = new Event('input', { bubbles: true, cancelable: true });
    element.dispatchEvent(inputEvent);

    return {
      success: true,
      action,
      timestamp: Date.now(),
      duration: Date.now() - startTime,
      elementFound: true
    };
  } catch (error) {
    return {
      success: false,
      action,
      error: error instanceof Error ? error.message : String(error),
      timestamp: Date.now(),
      duration: Date.now() - startTime
    };
  }
}
